/* GG 2029 Buy page.
   <gg29-buy>: gear filters and the wall/vehicle switch. Nothing is ever hidden: cards that fit move to the
   front, the rest go gray under a "Not made for ..." label and stay clickable. ?gear=skis preselects a filter.
   <gg29-buy-card>: one card that can switch products (colors, or bundle color + board). Hovering a swatch
   previews its photo; clicking selects it (photo, price, pack sizes, links and Add to cart follow). */

const GG29_COLORS = {
  cyan: '#00afee', black: '#17191c', carbon: '#3d4145', white: '#f4f4f2', wood: '#c9a27a', 'light wood': '#d8b88f',
  graphite: '#55595e', maple: '#e3c79c', birch: '#e8d6b5', slate: '#6b7178', gray: '#9a9ea3', grey: '#9a9ea3',
};

if (!customElements.get('gg29-buy-card')) {
  customElements.define(
    'gg29-buy-card',
    class Gg29BuyCard extends HTMLElement {
      connectedCallback() {
        try {
          this.options = JSON.parse(this.querySelector('[data-options]').textContent);
        } catch (e) {
          this.options = [];
        }
        if (!this.options.length) return;
        this.kind = this.dataset.kind;
        this.img = this.querySelector('.gg29-bcard__img');
        this.swatches = this.querySelector('[data-swatches]');
        this.packs = this.querySelector('[data-packs]');
        this.addButton = this.querySelector('[data-add]');
        this.error = this.querySelector('.gg29-bcard__error');
        this.current = this.options.find((o) => String(o.id) === this.dataset.selected) || this.options[0];

        this.buildSwatches();
        this.initializing = true;
        this.select(this.current, { initial: true });
        this.initializing = false;
        this.addButton.addEventListener('click', () => this.addToCart());
      }

      /* Swatches ---------------------------------------------------------------------------------- */
      buildSwatches() {
        this.swatches.innerHTML = '';
        if (this.kind === 'bundle') {
          const colors = [...new Set(this.options.map((o) => o.color))];
          const boards = [...new Set(this.options.map((o) => o.board))];
          if (colors.length > 1) this.swatches.append(this.makeRow('color', this.dataset.colorLabel, colors, true));
          if (boards.length > 1) this.swatches.append(this.makeRow('board', this.dataset.boardLabel, boards, false));
        } else if (this.options.length > 1) {
          this.swatches.append(this.makeRow('product', this.dataset.colorLabel, this.options.map((o) => o.color), true));
        }
        this.swatches.hidden = !this.swatches.children.length;
      }

      makeRow(dim, label, values, asDots) {
        const row = document.createElement('div');
        row.className = 'gg29-bcard__row';
        row.dataset.dim = dim;
        const head = document.createElement('p');
        head.className = 'gg29-bcard__row-label';
        head.innerHTML = `${label}: <span data-current></span>`;
        const list = document.createElement('div');
        list.className = asDots ? 'gg29-bcard__dots' : 'gg29-bcard__pills';
        list.setAttribute('role', 'radiogroup');
        list.setAttribute('aria-label', label);
        values.forEach((value, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.dataset.value = value;
          b.setAttribute('role', 'radio');
          if (asDots) {
            b.className = 'gg29-bcard__dot';
            b.title = value;
            b.innerHTML = `<span class="visually-hidden">${value}</span>`;
            const option = dim === 'product' ? this.options[i] : null;
            const hex = GG29_COLORS[value.toLowerCase()];
            if (option && option.swatch) b.style.backgroundImage = `url('${option.swatch}')`;
            else if (hex) b.style.background = hex;
          } else {
            b.className = 'gg29-bcard__pill';
            b.textContent = value;
          }
          const pick = () => (dim === 'product' ? this.options[i] : this.find(dim, value));
          b.addEventListener('mouseenter', () => this.preview(pick()));
          b.addEventListener('focus', () => this.preview(pick()));
          b.addEventListener('mouseleave', () => this.preview(this.current));
          b.addEventListener('blur', () => this.preview(this.current));
          b.addEventListener('click', () => {
            const option = pick();
            if (option) this.select(option);
          });
          list.append(b);
        });
        row.append(head, list);
        return row;
      }

      /* Bundle: keep the other choice, change this one. Falls back to any bundle with this value. */
      find(dim, value) {
        const other = dim === 'color' ? 'board' : 'color';
        return (
          this.options.find((o) => o[dim] === value && o[other] === this.current[other]) ||
          this.options.find((o) => o[dim] === value)
        );
      }

      /* The photo for a product: the variant matching the current picks (e.g. the 6-pack photo), else the product photo. */
      photoFor(option) {
        const names = option.options || [];
        const picks = this.selected || {};
        const v =
          (option.variants || []).find((x) => x.image && names.every((n, i) => picks[n] === undefined || x.options[i] === picks[n])) ||
          null;
        return v ? { image: v.image, srcset: v.srcset } : { image: option.image, srcset: option.srcset };
      }

      previewPick(name, value) {
        const names = this.current.options || [];
        const picks = { ...this.selected, [name]: value };
        const v = (this.current.variants || []).find((x) => x.image && names.every((n, i) => x.options[i] === picks[n]));
        if (!v || !this.img || this.img.dataset.shown === v.image) return;
        this.img.dataset.shown = v.image;
        this.img.srcset = v.srcset || '';
        this.img.src = v.image;
      }

      preview(option) {
        if (!option || !this.img) return;
        const photo = this.photoFor(option);
        if (!photo.image || this.img.dataset.shown === photo.image) return;
        this.img.dataset.shown = photo.image;
        this.img.srcset = photo.srcset || '';
        this.img.src = photo.image;
      }

      select(option, { initial = false } = {}) {
        this.current = option;
        if (!initial) this.preview(option);
        else if (this.img) this.img.dataset.shown = this.img.currentSrc || this.img.src;

        this.querySelectorAll('[data-link]').forEach((a) => (a.href = option.url));
        const media = this.querySelector('.gg29-bcard__media');
        if (media) media.href = option.url;

        this.swatches.querySelectorAll('.gg29-bcard__row').forEach((row) => {
          const dim = row.dataset.dim;
          const value = dim === 'product' ? option.color : option[dim];
          row.querySelector("[data-current]").textContent = value;
          row.querySelectorAll('button').forEach((b, i) => {
            const on = b.dataset.value === value;
            b.setAttribute('aria-checked', String(on));
            b.classList.toggle('is-on', on);
            // Bundles: gray out combinations that don't exist or are sold out.
            let target = null;
            if (dim === 'product') target = this.options[i];
            else if (this.kind === 'bundle') {
              const other = dim === 'color' ? 'board' : 'color';
              target = this.options.find((o) => o[dim] === b.dataset.value && o[other] === option[other]);
            }
            b.classList.toggle('is-unavailable', !target || !target.available);
          });
        });

        this.buildPacks(option);
      }

      /* Variant pickers: a color option shows as dots, every other option (pack size...) as buttons. ------ */
      buildPacks(option) {
        const names = option.options || [];
        const variants = option.variants || [];
        const prev = this.selected || {};
        const start =
          variants.find((v) => v.available && names.every((n, i) => prev[n] === undefined || v.options[i] === prev[n])) ||
          variants.find((v) => v.available) ||
          variants[0];
        this.selected = {};
        names.forEach((n, i) => (this.selected[n] = start ? start.options[i] : undefined));

        this.packs.innerHTML = '';
        names.forEach((name, i) => {
          const values = [...new Set(variants.map((v) => v.options[i]))];
          if (values.length < 2) return;
          const isColor = /colou?r/i.test(name);
          const row = document.createElement('div');
          row.className = 'gg29-bcard__row';
          row.dataset.option = name;
          if (isColor) {
            const head = document.createElement('p');
            head.className = 'gg29-bcard__row-label';
            head.innerHTML = `${name}: <span data-current></span>`;
            row.append(head);
          }
          const list = document.createElement('div');
          list.className = isColor ? 'gg29-bcard__dots' : 'gg29-bcard__packrow';
          list.setAttribute('role', 'radiogroup');
          list.setAttribute('aria-label', name);
          values.forEach((value) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.dataset.value = value;
            b.setAttribute('role', 'radio');
            if (isColor) {
              b.className = 'gg29-bcard__dot';
              b.title = value;
              b.innerHTML = `<span class="visually-hidden">${value}</span>`;
              const hex = GG29_COLORS[value.toLowerCase()];
              if (hex) b.style.background = hex;
            } else {
              b.className = 'gg29-bcard__pack';
              b.title = value;
              b.textContent = this.packLabel(value);
            }
            b.addEventListener('click', () => {
              this.selected[name] = value;
              this.updateVariant();
            });
            // Hover or focus previews that choice's photo; leaving puts the picked one back.
            const peek = () => this.previewPick(name, value);
            const back = () => this.preview(this.current);
            b.addEventListener('mouseenter', peek);
            b.addEventListener('focus', peek);
            b.addEventListener('mouseleave', back);
            b.addEventListener('blur', back);
            list.append(b);
          });
          row.append(list);
          this.packs.append(row);
        });
        this.packs.hidden = !this.packs.children.length;
        this.updateVariant();
      }

      /* "3-Pack" -> "3"; "3-Pack White Board" -> "3-Pack"; anything else as is. */
      packLabel(value) {
        if (/^\d+-pack$/i.test(value)) return value.replace(/-pack$/i, '');
        const m = value.match(/^\d+-pack\b/i);
        return m ? m[0] : value;
      }

      /* What the same number of singles would cost, for "N-Pack" variants without their own compare-at price. */
      packCompare(v) {
        if (v.compare) return v.compare;
        const mode = this.dataset.savings || 'exact';
        if (mode === 'off') return null;
        const variants = this.current.variants || [];
        const i = v.options.findIndex((o) => /^\d+-pack\b/i.test(o));
        if (i === -1) return null;
        const n = parseInt(v.options[i], 10);
        if (!(n > 1)) return null;
        const single = variants.find((x) => x.options.every((o, j) => (j === i ? /^1-pack\b/i.test(o) : o === v.options[j])));
        if (!single) return null;
        let compare = single.price * n;
        if (mode === 'rounded') compare = Math.round(compare / 100) * 100 - 1;
        return compare > v.price ? compare : null;
      }

      money(cents) {
        const format = window.gg29MoneyFormat || '${{amount}}';
        const fixed = (d, t, dec) => {
          const [a, b] = (cents / 100).toFixed(d).split('.');
          return a.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (b ? dec + b : '');
        };
        const out = format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
          if (key === 'amount_no_decimals') return fixed(0, ',', '.');
          if (key === 'amount_with_comma_separator') return fixed(2, '.', ',');
          if (key === 'amount_no_decimals_with_comma_separator') return fixed(0, '.', ',');
          return fixed(2, ',', '.');
        });
        return out.replace(/<[^>]*>/g, '');
      }

      /* "$20.00" -> "$20" */
      short(cents) {
        return this.money(cents).replace(/[.,]00(?!\d)/, '');
      }

      updateVariant() {
        const names = this.current.options || [];
        const variants = this.current.variants || [];
        const matches = (v, over = {}) => names.every((n, i) => v.options[i] === (n in over ? over[n] : this.selected[n]));
        const v = variants.find((x) => matches(x)) || null;
        this.variant = v;

        this.packs.querySelectorAll('.gg29-bcard__row').forEach((row) => {
          const name = row.dataset.option;
          const label = row.querySelector("[data-current]");
          if (label) label.textContent = this.selected[name];
          row.querySelectorAll('button').forEach((b) => {
            const on = this.selected[name] === b.dataset.value;
            b.setAttribute('aria-checked', String(on));
            b.classList.toggle('is-on', on);
            const target = variants.find((x) => matches(x, { [name]: b.dataset.value }));
            b.classList.toggle('is-unavailable', !target || !target.available);
          });
        });

        const price = this.querySelector('[data-price]');
        if (price && v) {
          const compare = this.packCompare(v);
          price.innerHTML =
            (compare ? `<s class="gg29-bcard__compare">${this.money(compare)}</s> ` : '') +
            `<span>${this.money(v.price)}</span>` +
            (compare ? ` <span class="gg29-bcard__save">Save ${this.short(compare - v.price)}</span>` : '');
        }
        if (!this.initializing) this.preview(this.current);
        const ok = v && v.available;
        this.addButton.disabled = !ok;
        this.addButton.textContent = ok ? this.addButton.dataset.label : this.addButton.dataset.soldOut;
        this.addButton.dataset.variant = v ? v.id : '';
      }

      /* Add to cart: same path as the product page, then the theme's cart drawer opens. -------------- */
      async addToCart() {
        const id = this.addButton.dataset.variant;
        if (!id) return;
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.addButton.classList.add('is-loading');
        this.addButton.setAttribute('aria-busy', 'true');
        this.error.hidden = true;
        try {
          const body = { id: Number(id), quantity: 1 };
          if (cart && cart.getSectionsToRender) {
            body.sections = cart.getSectionsToRender().map((s) => s.id);
            body.sections_url = window.location.pathname;
          }
          const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await res.json();
          if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
          if (cart && cart.renderContents) {
            cart.classList.remove('is-empty');
            cart.renderContents(data);
          } else {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          }
        } catch (e) {
          this.error.textContent = e.message;
          this.error.hidden = false;
        } finally {
          this.addButton.classList.remove('is-loading');
          this.addButton.removeAttribute('aria-busy');
        }
      }
    }
  );
}

if (!customElements.get('gg29-buy')) {
  customElements.define(
    'gg29-buy',
    class Gg29Buy extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-grid]');
        this.items = [...this.grid.querySelectorAll('.gg29-buy__item')];
        this.countEl = this.querySelector('[data-count]');
        this.gear = '*';
        this.place = '*';

        // Phones pin the "N products hold ..." bar under the theme's sticky header.
        const setTop = () => {
          const header = document.querySelector('sticky-header, .shopify-section-header-sticky');
          const sticky = header && ['sticky', 'fixed'].includes(getComputedStyle(header.closest('.shopify-section') || header).position);
          this.style.setProperty('--gg29-sticky-top', sticky ? `${header.offsetHeight}px` : '0px');
        };
        setTop();
        window.addEventListener('resize', setTop);

        this.divider = document.createElement('li');
        this.divider.className = 'gg29-buy__divider';
        this.divider.setAttribute('role', 'presentation');

        this.querySelector('.gg29-buy__chips')?.addEventListener('click', (e) => {
          const chip = e.target.closest('[data-filter]');
          if (chip) this.setGear(chip.dataset.filter);
        });
        this.querySelector('.gg29-buy__place')?.addEventListener('click', (e) => {
          const b = e.target.closest('[data-place]');
          if (!b) return;
          this.place = b.dataset.place;
          this.querySelectorAll('.gg29-buy__place-btn').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
          this.apply();
        });

        const wanted = new URLSearchParams(window.location.search).get('gear');
        if (wanted && this.querySelector(`[data-filter="${CSS.escape(wanted)}"]`)) this.setGear(wanted, { silent: true });
      }

      setGear(key, { silent = false } = {}) {
        this.gear = key;
        this.querySelectorAll('.gg29-buy__chips [data-filter]').forEach((c) =>
          c.setAttribute('aria-pressed', String(c.dataset.filter === key))
        );
        this.apply();
        if (!silent) {
          const url = new URL(window.location.href);
          key === '*' ? url.searchParams.delete('gear') : url.searchParams.set('gear', key);
          history.replaceState(history.state, '', url);
        }
      }

      label(key) {
        const chip = this.querySelector(`[data-filter="${CSS.escape(key)}"]`);
        return chip ? chip.textContent.trim().toLowerCase() : '';
      }

      apply() {
        const fits = [];
        const rest = [];
        this.items.forEach((item) => {
          const holds = (item.dataset.holds || '').split(' ');
          const where = item.dataset.place;
          const gearOk = this.gear === '*' || holds.includes(this.gear);
          const placeOk = this.place === '*' || where === this.place || where === 'both';
          const ok = gearOk && placeOk;
          item.classList.toggle('is-dim', !ok);
          (ok ? fits : rest).push(item);
          item.querySelectorAll('[data-fit]').forEach((p) => {
            p.hidden = !(ok && this.gear !== '*' && p.dataset.fit === this.gear);
          });
        });

        // Fitting cards first (in their normal order), then the gray ones under a label.
        const filtered = this.gear !== '*' || this.place !== '*';
        fits.forEach((i) => this.grid.append(i));
        if (filtered && rest.length && fits.length) {
          const placeBtn = this.querySelector(`.gg29-buy__place-btn[data-place="${this.place}"]`);
          const what = this.gear !== '*' ? this.label(this.gear) : placeBtn ? placeBtn.textContent.trim().toLowerCase() : '';
          this.divider.textContent = (this.dataset.dimText || '').replace('[gear]', what);
          this.grid.append(this.divider);
        } else {
          this.divider.remove();
        }
        rest.forEach((i) => this.grid.append(i));

        if (!filtered) {
          this.countEl.textContent = this.dataset.allText;
        } else {
          let text;
          if (this.gear !== '*') {
            const template = fits.length === 1 ? this.dataset.countOne : this.dataset.countText;
            text = template.replace('[count]', fits.length).replace('[gear]', this.label(this.gear));
          } else {
            text = `${fits.length} ${fits.length === 1 ? 'product' : 'products'}`;
          }
          if (this.place !== '*') {
            const b = this.querySelector(`.gg29-buy__place-btn[data-place="${this.place}"]`);
            if (b) text += ` · ${b.textContent.trim().toLowerCase()}`;
          }
          this.countEl.textContent = text;
        }
      }
    }
  );
}

if (!customElements.get('gg29-finish-wall')) {
  customElements.define(
    'gg29-finish-wall',
    class Gg29FinishWall extends HTMLElement {
      connectedCallback() {
        this.pins = [...this.querySelectorAll('.gg29-wall-addons__pin')];
        if (!this.pins.length) return;
        this.cards = [...this.querySelectorAll('.gg29-addon')];
        const light = (n, on) => {
          this.pins.forEach((p) => p.classList.toggle('is-lit', on && p.dataset.pin === n));
          this.cards.forEach((c) => c.classList.toggle('is-lit', on && c.dataset.pin === n));
        };
        this.pins.forEach((pin) => {
          const n = pin.dataset.pin;
          pin.addEventListener('mouseenter', () => light(n, true));
          pin.addEventListener('mouseleave', () => light(n, false));
          pin.addEventListener('click', () => {
            light(n, true);
            const card = this.cards.find((c) => c.dataset.pin === n);
            if (!card) return;
            // On phones the cards scroll sideways; bring the card into view without jumping the page.
            const list = card.parentElement;
            if (list.scrollWidth > list.clientWidth) list.scrollTo({ left: card.offsetLeft - list.offsetLeft, behavior: 'smooth' });
            else card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          });
        });
        this.cards.forEach((card) => {
          card.addEventListener('mouseenter', () => light(card.dataset.pin, true));
          card.addEventListener('mouseleave', () => light(card.dataset.pin, false));
        });
      }
    }
  );
}
